import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { eventAPI, bannersAPI, facebookAPI } from '../services/api';
import './EventManagement.css';

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('upcoming'); // default: show upcoming only; past events won't clutter the list
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showArchived, setShowArchived] = useState(false); // false = active events, true = past events (archived + past by date)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [banners, setBanners] = useState([]);
  const [showBannerSelector, setShowBannerSelector] = useState(false);
  const [selectedBannerFile, setSelectedBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  // Import from Facebook
  const [showFacebookImport, setShowFacebookImport] = useState(false);
  const [fbPosts, setFbPosts] = useState([]);
  const [fbPostsLoading, setFbPostsLoading] = useState(false);
  const [fbPostsError, setFbPostsError] = useState('');
  const [creatingEventId, setCreatingEventId] = useState(null);
  const [importedPostIds, setImportedPostIds] = useState(new Set());
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    long_description: '',
    category: 'Events',
    subcategory: '',
    event_type: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    address: '',
    district: '',
    latitude: '',
    longitude: '',
    max_attendees: '',
    registration_required: false,
    registration_url: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    banner_image: '',
    website_url: '',
    price_adult: '',
    price_child: '',
    currency: 'MYR',
    pricing_type: 'free',
    status: 'published',
    is_featured: false,
    is_top_event: false
  });

  // Categories and districts (you can expand these)
  const categories = ['Events', 'Conference', 'Festival', 'Workshop', 'Exhibition', 'Sports', 'Cultural', 'Educational'];
  const districts = ['MBMB', 'Jasin', 'Alor Gajah', 'Masjid Tanah'];
  const eventTypes = ['Conference', 'Festival', 'Workshop', 'Exhibition', 'Sports', 'Cultural', 'Educational', 'Entertainment', 'Business', 'Community'];
  const timeFilters = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'current', label: 'Current' },
    { value: 'past', label: 'Past' },
    { value: '', label: 'All Events' }
  ];
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' }
  ];

  useEffect(() => {
    const run = async () => {
      try {
        await eventAPI.archivePastEvents();
      } catch (err) {
        // Non-blocking: e.g. endpoint missing or DB has no is_archived column
      }
      fetchEvents();
      fetchBanners();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedCategory, selectedDistrict, selectedTimeFilter, selectedStatus, showArchived, searchTerm]);

  const fetchBanners = async () => {
    try {
      const response = await bannersAPI.getAll({ limit: 100 });
      if (response.success) {
        setBanners(response.data.banners || []);
      }
    } catch (err) {
      console.error('Failed to fetch banners:', err);
    }
  };

  // Export to CSV handler
  const handleExportCSV = async () => {
    try {
      // Check both 'token' and 'travel-futura-token' for backward compatibility
      const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
      const response = await fetch('/api/v1/events/export/csv', {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(errorData.message || 'Export failed');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `events_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Events exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert(`❌ Failed to export events: ${error.message}`);
    }
  };

  const handleBannerFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedBannerFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setBannerPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerUpload = async () => {
    if (!selectedBannerFile) return;
    
    try {
      setError('');
      const response = await eventAPI.uploadBannerImage(selectedBannerFile);
      
      if (response.success) {
        // Handle both single file response (data.url) and multiple files response (data.files[0].url)
        let imageUrl = '';
        if (response.data && response.data.url) {
          // Single file upload response
          imageUrl = response.data.url;
        } else if (response.data && response.data.files && response.data.files.length > 0) {
          // Multiple files upload response (first file)
          imageUrl = response.data.files[0].url;
        } else {
          throw new Error('Invalid upload response format');
        }
        
        // Ensure URL is absolute
        if (imageUrl && imageUrl.startsWith('/')) {
          const apiBase = process.env.REACT_APP_API_URL || '';
          imageUrl = `${apiBase}${imageUrl}`;
        }
        
        // Log for debugging
        console.log('✅ Image uploaded successfully. URL:', imageUrl);
        console.log('📝 Setting formData.banner_image to:', imageUrl);
        
        // Update formData with the image URL - use functional update to ensure we have latest state
        setFormData(prevFormData => {
          const updated = {...prevFormData, banner_image: imageUrl};
          console.log('📦 Updated formData:', { ...updated, banner_image: updated.banner_image });
          return updated;
        });
        
        setSelectedBannerFile(null);
        setBannerPreview(null);
        setSuccess('Banner image uploaded successfully! URL saved.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to upload banner image: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Banner upload error:', err);
      setError('Failed to upload banner image: ' + (err.response?.data?.message || err.message));
    }
  };

  const selectExistingBanner = (banner) => {
    setFormData({...formData, banner_image: banner.image_url});
    setShowBannerSelector(false);
  };

  const fetchEvents = async () => {
    const viewingArchived = showArchived;
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 50,
        category: selectedCategory,
        district: selectedDistrict,
        status: selectedStatus === 'all' ? '' : selectedStatus,
        search: searchTerm,
        archived: viewingArchived ? 'true' : 'false'
      };

      let response;
      if (viewingArchived) {
        response = await eventAPI.getAllEvents(params);
      } else if (selectedTimeFilter) {
        response = await eventAPI.getEventsByTimeFilter(selectedTimeFilter, params);
      } else {
        response = await eventAPI.getAllEvents(params);
      }

      if (response.success && showArchived === viewingArchived) {
        setEvents(response.data.events);
        setTotalPages(response.data.pagination.total_pages);
      } else if (!response.success) {
        setError('Failed to fetch events');
      }
    } catch (err) {
      setError('Error fetching events: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchEvents();
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      setError(''); // Clear any previous errors
      setSuccess(''); // Clear any previous success messages
      
      const response = await eventAPI.createEvent(formData);
      if (response.success) {
        setShowCreateForm(false);
        setFormData({
          title: '',
          description: '',
          long_description: '',
          category: 'Events',
          subcategory: '',
          event_type: '',
          start_date: '',
          end_date: '',
          start_time: '',
          end_time: '',
          address: '',
          district: '',
          latitude: '',
          longitude: '',
          max_attendees: '',
          registration_required: false,
          registration_url: '',
          contact_person: '',
          contact_email: '',
          contact_phone: '',
          banner_image: '',
          website_url: '',
          price_adult: '',
          price_child: '',
          currency: 'MYR',
          pricing_type: 'free',
          status: 'published',
          is_featured: false,
          is_top_event: false
        });
        
        // Set success message
        setSuccess('Event created successfully!');
        
        // Refresh the events list
        await fetchEvents();
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess('');
        }, 5000);
      } else {
        setError('Failed to create event');
      }
    } catch (err) {
      setError('Error creating event: ' + err.message);
    }
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      setError(''); // Clear any previous errors
      setSuccess(''); // Clear any previous success messages
      
      // Log what we're sending for debugging
      console.log('📤 Updating event with formData:', {
        id: editingEvent.id,
        banner_image: formData.banner_image || 'NOT SET/EMPTY',
        banner_image_length: formData.banner_image ? formData.banner_image.length : 0,
        title: formData.title,
        full_formData: formData
      });
      
      // Ensure banner_image is included if it exists
      const updatePayload = {...formData};
      if (!updatePayload.banner_image || updatePayload.banner_image.trim() === '') {
        console.warn('⚠️ banner_image is empty or not set in formData before update');
      } else {
        console.log('✅ banner_image will be sent:', updatePayload.banner_image);
      }
      
      const response = await eventAPI.updateEvent(editingEvent.id, updatePayload);
      if (response.success) {
        setEditingEvent(null);
        setFormData({
          title: '',
          description: '',
          long_description: '',
          category: 'Events',
          subcategory: '',
          event_type: '',
          start_date: '',
          end_date: '',
          start_time: '',
          end_time: '',
          address: '',
          district: '',
          latitude: '',
          longitude: '',
          max_attendees: '',
          registration_required: false,
          registration_url: '',
          contact_person: '',
          contact_email: '',
          contact_phone: '',
          banner_image: '',
          website_url: '',
          price_adult: '',
          price_child: '',
          currency: 'MYR',
          pricing_type: 'free',
          status: 'published',
          is_featured: false,
          is_top_event: false
        });
        
        // Set success message
        setSuccess('Event updated successfully!');
        
        // Refresh the events list
        await fetchEvents();
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess('');
        }, 5000);
      } else {
        setError('Failed to update event');
      }
    } catch (err) {
      setError('Error updating event: ' + err.message);
    }
  };

  const handleArchiveEvent = async (id) => {
    if (!window.confirm('Move this event to Past Events? It will be hidden from the public. You can restore it from Past Events.')) return;
    try {
      const response = await eventAPI.archiveEvent(id);
      if (response.success) {
        setSuccess('Event moved to Past Events.');
        setTimeout(() => setSuccess(''), 3000);
        fetchEvents();
      } else setError(response.message || 'Failed to archive');
    } catch (err) {
      setError('Failed to archive event: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUnarchiveEvent = async (id) => {
    try {
      const response = await eventAPI.unarchiveEvent(id);
      if (response.success) {
        setSuccess('Event restored to Active.');
        setTimeout(() => setSuccess(''), 3000);
        fetchEvents();
      } else setError(response.message || 'Failed to unarchive');
    } catch (err) {
      setError('Failed to unarchive event: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteEvent = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        const response = await eventAPI.deleteEvent(id);
        if (response.success) {
          fetchEvents();
        } else {
          setError('Failed to delete event');
        }
      } catch (err) {
        setError('Error deleting event: ' + err.message);
      }
    }
  };

  const handleToggleFeatured = async (id, currentStatus) => {
    try {
      const response = await eventAPI.toggleFeatured(id);
      if (response.success) {
        fetchEvents();
      } else {
        setError('Failed to toggle featured status');
      }
    } catch (err) {
      setError('Error toggling featured status: ' + err.message);
    }
  };

  const handleToggleTopEvent = async (id, currentStatus) => {
    try {
      const response = await eventAPI.toggleTopEvent(id);
      if (response.success) {
        fetchEvents();
      } else {
        setError('Failed to toggle top event status');
      }
    } catch (err) {
      setError('Error toggling top event status: ' + err.message);
    }
  };

  const fetchFacebookPosts = async () => {
    setFbPostsError('');
    setFbPostsLoading(true);
    setFbPosts([]);
    try {
      const res = await facebookAPI.getPosts(25);
      if (res.success && res.data && res.data.posts) {
        setFbPosts(res.data.posts);
      } else {
        setFbPostsError(res.message || 'Failed to load Facebook posts');
      }
    } catch (err) {
      setFbPostsError(err.response?.data?.message || err.message || 'Failed to fetch posts');
      setFbPosts([]);
    } finally {
      setFbPostsLoading(false);
    }
  };

  const handleCreateEventFromPost = async (post) => {
    setCreatingEventId(post.id);
    setError('');
    try {
      const res = await facebookAPI.createEventFromPost(post.id);
      if (res.success && res.data && res.data.event) {
        setImportedPostIds((prev) => new Set(prev).add(post.id));
        setSuccess(`Draft event "${res.data.event.title}" created. Add date & venue then publish.`);
        setTimeout(() => setSuccess(''), 5000);
        fetchEvents();
        const created = res.data.event;
        handleEditEvent(created);
      } else {
        setError(res.message || 'Failed to create event');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create event';
      setError(msg);
      if (err.response?.status === 409) {
        setImportedPostIds((prev) => new Set(prev).add(post.id));
      }
    } finally {
      setCreatingEventId(null);
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    
    // Ensure banner_image URL is absolute
    let bannerImageUrl = event.banner_image || '';
    if (bannerImageUrl && bannerImageUrl.startsWith('/')) {
      const apiBase = process.env.REACT_APP_API_URL || '';
      bannerImageUrl = `${apiBase}${bannerImageUrl}`;
    }
    
    setFormData({
      title: event.title || '',
      description: event.description || '',
      long_description: event.long_description || '',
      category: event.category || 'Events',
      subcategory: event.subcategory || '',
      event_type: event.event_type || '',
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      address: event.address || '',
      district: event.district || '',
      latitude: event.latitude || '',
      longitude: event.longitude || '',
      max_attendees: event.max_attendees || '',
      registration_required: event.registration_required || false,
      registration_url: event.registration_url || '',
      contact_person: event.contact_person || '',
      contact_email: event.contact_email || '',
      contact_phone: event.contact_phone || '',
      banner_image: bannerImageUrl,
      website_url: event.website_url || '',
      price_adult: event.price_adult || '',
      price_child: event.price_child || '',
      currency: event.currency || 'MYR',
      pricing_type: event.pricing_type || 'free',
      status: event.status || 'draft',
      is_featured: event.is_featured || false,
      is_top_event: event.is_top_event || false
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5); // Remove seconds
  };

  const getEventStatus = (event) => {
    const today = new Date();
    const startDate = new Date(event.start_date);
    const endDate = event.end_date ? new Date(event.end_date) : startDate;

    if (endDate < today) return 'Past';
    if (startDate <= today && endDate >= today) return 'Current';
    return 'Upcoming';
  };

  const getEventStatusColor = (status) => {
    switch (status) {
      case 'Past': return '#6c757d';
      case 'Current': return '#28a745';
      case 'Upcoming': return '#007bff';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <Layout title="Event Management">
        <div className="event-management">
          <div className="loading">Loading events...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Event Management">
      <div className="event-management">
      <div className="page-header">
        <button 
          className="btn-export"
          onClick={handleExportCSV}
          title="Export all events to CSV"
        >
          📥 Export to CSV
        </button>
        <button 
          className="btn-facebook-import"
          onClick={() => {
            setShowFacebookImport(!showFacebookImport);
            if (!showFacebookImport) {
              setFbPosts([]);
              setFbPostsError('');
            }
          }}
          title="Import events from Melaka Tourism Facebook page"
        >
          📘 Import from Facebook
        </button>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateForm(true)}
        >
          + Create New Event
        </button>
      </div>

      {/* Import from Facebook panel */}
      {showFacebookImport && (
        <div className="facebook-import-panel">
          <h3>Import from Melaka Tourism Facebook</h3>
          <p className="facebook-import-hint">Fetch recent posts and create a draft event from any post. You can then add date, venue and publish.</p>
          <button
            type="button"
            className="btn-fetch-posts"
            onClick={fetchFacebookPosts}
            disabled={fbPostsLoading}
          >
            {fbPostsLoading ? 'Loading posts…' : 'Fetch recent posts'}
          </button>
          {fbPostsError && (
            <div className="facebook-import-error">{fbPostsError}</div>
          )}
          {fbPosts.length > 0 && (
            <div className="facebook-posts-list">
              {fbPosts.map((post) => {
                const isImported = importedPostIds.has(post.id);
                const isCreating = creatingEventId === post.id;
                return (
                  <div key={post.id} className="facebook-post-card">
                    <div className="facebook-post-thumb">
                      {post.full_picture ? (
                        <img src={post.full_picture} alt="" />
                      ) : (
                        <div className="facebook-post-no-image">No image</div>
                      )}
                    </div>
                    <div className="facebook-post-body">
                      <p className="facebook-post-message">
                        {post.message ? (post.message.length > 200 ? post.message.slice(0, 200) + '…' : post.message) : '(No text)'}
                      </p>
                      {post.created_time && (
                        <span className="facebook-post-date">
                          {new Date(post.created_time).toLocaleDateString()}
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn-create-from-post"
                        onClick={() => handleCreateEventFromPost(post)}
                        disabled={isImported || isCreating}
                      >
                        {isCreating ? 'Creating…' : isImported ? 'Imported' : 'Create event from this post'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn-search">Search</button>
        </form>

        <div className="filter-group">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={selectedDistrict}
            onChange={(e) => setSelectedDistrict(e.target.value)}
            className="filter-select"
          >
            <option value="">All Districts</option>
            {districts.map(district => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>

          <select
            value={selectedTimeFilter}
            onChange={(e) => setSelectedTimeFilter(e.target.value)}
            className="filter-select"
          >
            {timeFilters.map(filter => (
              <option key={filter.value} value={filter.value}>{filter.label}</option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          <div className="archive-toggle" title="Switch between active (upcoming/current) and past events">
            <span className="archive-toggle-label">View:</span>
            <button
              type="button"
              className={`btn-archive-toggle ${!showArchived ? 'active' : ''}`}
              onClick={() => { setShowArchived(false); setCurrentPage(1); setEvents([]); }}
            >
              Active events
            </button>
            <button
              type="button"
              className={`btn-archive-toggle ${showArchived ? 'active' : ''}`}
              onClick={() => { setShowArchived(true); setCurrentPage(1); setEvents([]); }}
            >
              Past Events
            </button>
          </div>
        </div>
      </div>

      {showArchived && (
        <div className="archive-notice">
          Viewing <strong>Past Events</strong>. All past and archived events are listed here. They are hidden from the public. Use &quot;Restore to active&quot; to show an event in Active again.
        </div>
      )}
      {!showArchived && (selectedTimeFilter === 'past' || selectedTimeFilter === '') && (
        <div className="archive-tip">
          Tip: Move events to <strong>Past Events</strong> so they don’t appear on the public site. Open &quot;Past Events&quot; above to view or restore them.
        </div>
      )}

      {/* Events List */}
      <div className="events-list">
        {events.length === 0 ? (
          <div className="no-events">No events found</div>
        ) : (
          events.map(event => (
            <div key={event.id} className="event-card">
              <div className="event-header">
                <h3>{event.title}</h3>
                <div className="event-status">
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getEventStatusColor(getEventStatus(event)) }}
                  >
                    {getEventStatus(event)}
                  </span>
                  <span className={`status-indicator ${event.status}`}>
                    {event.status}
                  </span>
                </div>
              </div>

              <div className="event-details">
                {event.is_archived && (
                  <div className="event-badges" style={{ marginBottom: '8px' }}>
                    <span className="badge archived">Moved to past</span>
                  </div>
                )}
                <div className="event-info">
                  <p><strong>Category:</strong> {event.category}</p>
                  <p><strong>Type:</strong> {event.event_type}</p>
                  <p><strong>Date:</strong> {formatDate(event.start_date)}</p>
                  {event.end_date && event.end_date !== event.start_date && (
                    <p><strong>End Date:</strong> {formatDate(event.end_date)}</p>
                  )}
                  {event.start_time && (
                    <p><strong>Time:</strong> {formatTime(event.start_time)}</p>
                  )}
                  {event.address && (
                    <p><strong>Location:</strong> {event.address}</p>
                  )}
                  {event.max_attendees && (
                    <p><strong>Max Attendees:</strong> {event.max_attendees}</p>
                  )}
                  {event.price_adult && (
                    <p><strong>Price:</strong> {event.currency} {event.price_adult}</p>
                  )}
                </div>

                <div className="event-description">
                  <p>{event.description}</p>
                </div>

                <div className="event-badges">
                  {event.is_featured && <span className="badge featured">Featured</span>}
                  {event.is_top_event && <span className="badge top">Top Event</span>}
                  {event.registration_required && <span className="badge registration">Registration Required</span>}
                </div>
              </div>

              <div className="event-actions">
                {showArchived ? (
                  <button
                    className="btn-unarchive"
                    onClick={() => handleUnarchiveEvent(event.id)}
                    title="Restore event to Active events"
                  >
                    Restore to active
                  </button>
                ) : (
                  <button
                    className="btn-archive"
                    onClick={() => handleArchiveEvent(event.id)}
                    title="Move to Past Events (hidden from public)"
                  >
                    📁 Move to Past
                  </button>
                )}
                <button 
                  className="btn-edit"
                  onClick={() => handleEditEvent(event)}
                >
                  Edit
                </button>
                {!showArchived && (
                  <>
                    <button 
                      className={`btn-toggle ${event.is_featured ? 'active' : ''}`}
                      onClick={() => handleToggleFeatured(event.id, event.is_featured)}
                    >
                      {event.is_featured ? '★ Featured' : '☆ Feature'}
                    </button>
                    <button 
                      className={`btn-toggle ${event.is_top_event ? 'active' : ''}`}
                      onClick={() => handleToggleTopEvent(event.id, event.is_top_event)}
                    >
                      {event.is_top_event ? '🔥 Top' : '🔥 Make Top'}
                    </button>
                  </>
                )}
                <button 
                  className="btn-delete"
                  onClick={() => handleDeleteEvent(event.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-pagination"
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {(showCreateForm || editingEvent) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : 'Create New Event'}</h2>
              <button 
                className="btn-close"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingEvent(null);
                  setFormData({
                    title: '',
                    description: '',
                    long_description: '',
                    category: 'Events',
                    subcategory: '',
                    event_type: '',
                    start_date: '',
                    end_date: '',
                    start_time: '',
                    end_time: '',
                    address: '',
                    district: '',
                    latitude: '',
                    longitude: '',
                    max_attendees: '',
                    registration_required: false,
                    registration_url: '',
                    contact_person: '',
                    contact_email: '',
                    contact_phone: '',
                    banner_image: '',
                    website_url: '',
                    price_adult: '',
                    price_child: '',
                    currency: 'MYR',
                    pricing_type: 'free',
                    status: 'published',
                    is_featured: false,
                    is_top_event: false
                  });
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent} className="event-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Event Type</label>
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                  >
                    <option value="">Select Event Type</option>
                    {eventTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Subcategory</label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                    placeholder="Enter subcategory"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Long Description</label>
                <textarea
                  value={formData.long_description}
                  onChange={(e) => setFormData({...formData, long_description: e.target.value})}
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <select
                    value={formData.district}
                    onChange={(e) => setFormData({...formData, district: e.target.value})}
                  >
                    <option value="">Select District</option>
                    {districts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Max Attendees</label>
                  <input
                    type="number"
                    value={formData.max_attendees}
                    onChange={(e) => setFormData({...formData, max_attendees: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price Adult</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price_adult}
                    onChange={(e) => setFormData({...formData, price_adult: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Price Child</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price_child}
                    onChange={(e) => setFormData({...formData, price_child: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  >
                    <option value="MYR">MYR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Pricing Type</label>
                  <select
                    value={formData.pricing_type}
                    onChange={(e) => setFormData({...formData, pricing_type: e.target.value})}
                  >
                    <option value="free">Free</option>
                    <option value="paid">Paid</option>
                    <option value="donation">Donation</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Banner Image</label>
                <div className="banner-upload-section">
                  <div className="banner-options">
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={() => setShowBannerSelector(true)}
                    >
                      Select from Banner Management
                    </button>
                    <span className="or-text">OR</span>
                    <div className="file-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerFileSelect}
                        style={{ display: 'none' }}
                        id="banner-upload"
                      />
                      <label htmlFor="banner-upload" className="btn-secondary">
                        Upload New Image
                      </label>
                    </div>
                  </div>
                  
                  {bannerPreview && (
                    <div className="banner-preview">
                      <img src={bannerPreview} alt="Banner Preview" />
                      <button 
                        type="button" 
                        className="btn-primary"
                        onClick={handleBannerUpload}
                      >
                        Upload Banner
                      </button>
                    </div>
                  )}
                  
                  {formData.banner_image && (
                    <div className="current-banner">
                      <label>Current Banner:</label>
                      <img 
                        src={formData.banner_image} 
                        alt="Current Banner" 
                        style={{maxWidth: '200px', maxHeight: '100px'}}
                        onError={(e) => {
                          console.error('❌ Banner image failed to load:', formData.banner_image);
                          // Try to convert relative URL to absolute
                          if (formData.banner_image.startsWith('/')) {
                            const apiBase = process.env.REACT_APP_API_URL || '';
                            e.target.src = `${apiBase}${formData.banner_image}`;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                      <button 
                        type="button" 
                        className="btn-danger"
                        onClick={() => setFormData({...formData, banner_image: ''})}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  
                  <div className="banner-url-input">
                    <label>Or enter image URL:</label>
                    <input
                      type="url"
                      value={formData.banner_image || ''}
                      onChange={(e) => {
                        console.log('🔗 URL field changed:', e.target.value);
                        setFormData({...formData, banner_image: e.target.value});
                      }}
                      placeholder="https://example.com/image.jpg"
                    />
                    {formData.banner_image && formData.banner_image.trim() !== '' && (
                      <small style={{ color: '#28a745', display: 'block', marginTop: '4px' }}>
                        ✅ Image URL is set: {formData.banner_image.substring(0, 60)}...
                      </small>
                    )}
                    {(!formData.banner_image || formData.banner_image.trim() === '') && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>
                        ⚠️ No image URL set. Upload an image or enter a URL above.
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.registration_required}
                      onChange={(e) => setFormData({...formData, registration_required: e.target.checked})}
                    />
                    Registration Required
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    />
                    Featured Event
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_top_event}
                      onChange={(e) => setFormData({...formData, is_top_event: e.target.checked})}
                    />
                    Top Event
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowCreateForm(false);
                  setEditingEvent(null);
                }} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banner Selector Modal */}
      {showBannerSelector && (
        <div className="modal-overlay">
          <div className="modal-content banner-selector-modal">
            <div className="modal-header">
              <h3>Select Banner from Banner Management</h3>
              <button 
                className="close-btn"
                onClick={() => setShowBannerSelector(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="banner-grid">
                {banners.map(banner => (
                  <div 
                    key={banner.id} 
                    className="banner-item"
                    onClick={() => selectExistingBanner(banner)}
                  >
                    {banner.image_url && (
                      <img 
                        src={banner.image_url} 
                        alt={banner.title}
                        className="banner-thumbnail"
                      />
                    )}
                    <div className="banner-info">
                      <h4>{banner.title}</h4>
                      <p>{banner.subtitle}</p>
                      <span className="banner-status">
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {banners.length === 0 && (
                <p className="no-banners">No banners available. Create some banners first in Banner Management.</p>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default EventManagement;
